import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import MapView from "@/components/MapView";
import MultiSelect, { NONE_TOKEN } from "@/components/MultiSelect";
import CircuitFilter from "@/components/CircuitFilter";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, MapPin, Trophy, Users, User, Dumbbell, Calendar, Plus, Filter, BadgeCheck, X, Share2, LocateFixed, Loader2, Eraser, MessageSquare, Check, ChevronDown } from "lucide-react";
import { SPORTS, DEFAULT_SPORT, agesForSport, GRADES, ALL_AGES, collapseRanges, collapseYearRanges, eventAudienceParts } from "@/lib/sports";
import { StarRating, useIsMobile } from "@/components/listingUI";
import { useCircuitConfig } from "@/lib/circuits";
import { useMyLoc } from "@/lib/geoLocation";
import { sportFromSlug, sportFromSubdomain, sportToSlug } from "@/lib/sportRoutes";
import { gradeFilterOptions, gradeToGradYear, gradeOneBelow } from "@/lib/gradYears";
import { EventAudience } from "@/components/EventAudience";
import { heroForSport } from "@/lib/sportHeros";
import HeroSlideshow from "@/components/HeroSlideshow";
import InListAd from "@/components/InListAd";
import Seo from "@/components/Seo";
import { US_STATES } from "@/lib/usStates";
import { circuitPageMeta } from "@/lib/circuitPages";
import { EVENT_TYPE_OPTIONS } from "@/lib/eventTypes";
import { ABBR_TO_STATE, stateSlug, circuitSlug } from "@/lib/seo";
import { getSavedFilters, saveFilters, getListPrefs, saveListPrefs } from "@/lib/filterCookies";
import { useAuth } from "@/context/AuthContext";
import { useSport } from "@/context/SportContext";
import SportIcon from "@/components/SportIcon";
import SaveSearchButton from "@/components/SaveSearchButton";
import { toast } from "sonner";
import ProgramCard from "@/components/ProgramCard";
import TrendingStrip from "@/components/TrendingStrip";
import { groupIntoPrograms } from "@/lib/programs";
import { formatDate, formatDateRange, formatTimeRange, todayLocalISO } from "@/lib/formatDate";
import { tzAbbrev } from "@/lib/timezone";

const STATES = ["FL", "TX", "GA", "CA", "OH", "NY", "PA", "IL", "CO", "AZ", "WA", "MA", "DC", "NC", "NJ", "MN", "MI", "MO", "VA", "WI", "IN", "TN"];
const GENDERS = [["M", "Boys"], ["F", "Girls"]];
const PLAYER_VISIBLE_PROFILES = new Set(["scout", "team_director", "trainer"]);

// Max-travel slider: 100→3000 mi in smooth 25-mi steps, with a final "Unlimited" notch.
const RADIUS_MIN = 100;
const RADIUS_MAX = 3000;           // fallback ceiling before the furthest distance is known
const RADIUS_HARD_MAX = 5000;      // absolute cap (matches backend radius_miles limit)
const RADIUS_STEP = 25;

// Local (not UTC) today as YYYY-MM-DD — events default to today onward.
const TODAY_ISO = (() => {
    const d = new Date();
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
})();

export default function Landing({ forcedState = null, forcedCircuit = null }) {
    const navigate = useNavigate();
    const { user, feedBadgeTotal } = useAuth();
    const { sport: sportParam } = useParams();

    // The path segment (/basketball) or a legacy <sport>.travelsports.info host sets
    // the initial sport, overriding the cookie. The picker stays interactive everywhere.
    const urlSport = sportFromSlug(sportParam) || sportFromSubdomain();

    // A single-segment path that isn't one of our sports (e.g. a stale link) → home.
    useEffect(() => {
        if (sportParam && !sportFromSlug(sportParam)) navigate("/", { replace: true });
    }, [sportParam, navigate]);

    // SEO state landing pages (/basketball/texas) force that one state selected.
    const forcedStateName = forcedState ? ABBR_TO_STATE[forcedState] : null;
    const circuitPageInfo = forcedCircuit ? circuitPageMeta(forcedCircuit) : null;

    // Initial filter state — loaded from cookie for persistence across sessions.
    const cookieInit = getSavedFilters({
        search: "",
        sport: DEFAULT_SPORT,     // single-select
        domains: ["team", "trainer", "event"],
        states: [],
        ages: [],
        grades: [],
        genders: [],
        circuits: [],
        near_zip: "",
        radius_miles: "",
        events_when: "upcoming", // 'upcoming' | 'past'
        view: "map",             // 'map' | 'list'
    });
    // Legacy cookies may still hold sports: []; migrate to single value.
    if (Array.isArray(cookieInit.sports)) {
        cookieInit.sport = cookieInit.sports[0] || DEFAULT_SPORT;
        delete cookieInit.sports;
    }
    // URL sport always takes precedence on mount
    const base = urlSport ? { ...cookieInit, sport: urlSport } : cookieInit;

    // Shareable-link query params (?zip=&radius=&sport=&domains=&states=&…) override the
    // cookie so a saved-alert "Share" link opens the search pre-filtered.
    const fromQuery = (() => {
        if (typeof window === "undefined") return {};
        const qp = new URLSearchParams(window.location.search);
        if (![...qp.keys()].some((k) => ["q", "sport", "domains", "states", "ages", "grades", "genders", "circuits", "types", "zip", "radius"].includes(k))) return {};
        const list = (k) => (qp.get(k) != null ? (qp.get(k) ? qp.get(k).split(",") : []) : undefined);
        const out = {};
        if (qp.get("q") != null) out.search = qp.get("q");
        if (qp.get("sport")) out.sport = qp.get("sport");
        if (list("domains") !== undefined) out.domains = list("domains");
        if (list("states") !== undefined) out.states = list("states");
        if (list("ages") !== undefined) out.ages = list("ages");
        if (list("grades") !== undefined) out.grades = list("grades");
        if (list("genders") !== undefined) out.genders = list("genders");
        if (list("circuits") !== undefined) out.circuits = list("circuits");
        if (list("types") !== undefined) out.eventTypes = list("types");
        if (qp.get("zip") != null) out.near_zip = qp.get("zip");
        if (qp.get("radius") != null) out.radius_miles = qp.get("radius");
        return out;
    })();
    // A forced landing page (state OR circuit) is URL-authoritative: it clears the
    // other dimension so the page content, URL, title and canonical always agree
    // (e.g. arriving on /basketball/texas never inherits a cookie-saved circuit).
    const initial = {
        ...base,
        ...fromQuery,
        ...(forcedState ? { states: [forcedState], circuits: [] } : {}),
        ...(forcedCircuit ? { circuits: [forcedCircuit], states: [], domains: ["team", "event"] } : {}),
    };

    const [search, setSearch] = useState(initial.search);
    const [sport, setSport] = useState(initial.sport || DEFAULT_SPORT);
    const [domains, setDomains] = useState(initial.domains);
    const [states, setStates] = useState(initial.states);
    const [ages, setAges] = useState(initial.ages);
    const [grades, setGrades] = useState(Array.isArray(initial.grades) ? initial.grades : []);
    const [genders, setGenders] = useState(initial.genders);
    const [circuits, setCircuits] = useState(initial.circuits);
    const [eventTypes, setEventTypes] = useState(initial.eventTypes || []);
    const [typesOpen, setTypesOpen] = useState(false);
    // Athlete-specific filters (Pro members only) — position multiselect + grad year + height/age.
    const [athPositions, setAthPositions] = useState([]);
    const [athGradYears, setAthGradYears] = useState([]);
    const [athHeightMin, setAthHeightMin] = useState("");
    const [athAgeMax, setAthAgeMax] = useState("");
    const [booted, setBooted] = useState(false);
    const [nearZip, setNearZip] = useState(initial.near_zip);
    const [locating, setLocating] = useState(false);
    // Precise device location (lat/lng) from "use my location" — used as the map center point
    // so a close zoom reveals exactly where the visitor is relative to listings. Cleared when
    // the ZIP is typed manually (then the ZIP centroid is used instead).
    const [myLoc, setMyLoc] = useMyLoc();
    const useMyLocation = () => {
        if (!navigator.geolocation) { toast.error("Location not supported on this device"); return; }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { data } = await api.get("/geo/reverse-zip", { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
                    if (data?.zip) {
                        setMyLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude });
                        setNearZip(data.zip);
                        toast.success(`Using your location · ${data.zip}`);
                    }
                    else toast.error("Couldn't determine your ZIP code");
                } catch { toast.error("Couldn't determine your ZIP code"); }
                finally { setLocating(false); }
            },
            () => { setLocating(false); toast.error("Location permission denied"); },
            { timeout: 8000, enableHighAccuracy: true }
        );
    };

    // On load, if location permission was already granted, silently use the device's
    // precise coords so result-card distances are accurate — no button tap needed.
    const didAutoLoc = useRef(false);
    useEffect(() => {
        if (didAutoLoc.current || !navigator.permissions?.query || !navigator.geolocation) return;
        didAutoLoc.current = true;
        navigator.permissions.query({ name: "geolocation" }).then((res) => {
            if (res.state !== "granted") return;
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                    setMyLoc(c);
                    try {
                        const { data } = await api.get("/geo/reverse-zip", { params: c });
                        if (data?.zip) setNearZip((z) => z || data.zip);
                    } catch { /* ignore */ }
                },
                () => {},
                { timeout: 8000, enableHighAccuracy: true }
            );
        }).catch(() => {});
    }, []);
    const [radius, setRadius] = useState(initial.radius_miles);
    const [eventsFrom, setEventsFrom] = useState(initial.events_from && initial.events_from >= TODAY_ISO ? initial.events_from : TODAY_ISO);
    const [eventsTo, setEventsTo] = useState(initial.events_to || "");
    const [view, setView] = useState(initial.view === "list" ? "list" : "map");

    // Two-level sticky offsets for the list view: the Map/List bar pins under the site header,
    // and the section headers pin under the bar. Track their heights as CSS vars.
    useEffect(() => {
        const header = document.querySelector('[data-testid="site-header"]');
        const bar = document.querySelector('[data-testid="list-sticky-bar-inner"]');
        const measure = () => {
            const h = header ? header.offsetHeight : 64;
            document.documentElement.style.setProperty("--tsi-sticky-top", `${h}px`);
            document.documentElement.style.setProperty("--tsi-list-top", `${h + (bar ? bar.offsetHeight : 0)}px`);
        };
        measure();
        const ro = new ResizeObserver(measure);
        if (header) ro.observe(header);
        if (bar) ro.observe(bar);
        window.addEventListener("resize", measure);
        return () => { ro.disconnect(); window.removeEventListener("resize", measure); };
    }, [view]);
    // The in-flow Map/List controls sit to the right of the range box. When the toolbar
    // scrolls under the header, a fixed copy pins and appears left-aligned with the section headers.
    const [stuck, setStuck] = useState(false);
    const stickySentinelRef = useRef(null);
    useEffect(() => {
        const onScroll = () => {
            const sentinel = stickySentinelRef.current;
            const header = document.querySelector('[data-testid="site-header"]');
            const barInner = document.querySelector('[data-testid="list-sticky-bar-inner"]');
            if (!sentinel || !header) return;
            const headerBottom = header.getBoundingClientRect().bottom;
            const barH = barInner ? barInner.offsetHeight : 48;
            const sentinelTop = sentinel.getBoundingClientRect().top;
            setStuck(sentinelTop <= headerBottom + barH);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
    }, [view]);
    const [searchOpen, setSearchOpen] = useState(false);
    const resultsRef = useRef(null);
    const fetchSeq = useRef(0);
    const maxDistSeq = useRef(0);
    const goToResults = (v) => {
        setView(v);
        setSearchOpen(false);
        setRadius("");
        setTimeout(() => { if (resultsRef.current) resultsRef.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80);
    };

    // Player browsing is only available when the active profile is Scout or Team Director / Coach.
    const canBrowsePlayers = !!user && (PLAYER_VISIBLE_PROFILES.has(user.active_profile) || user.is_admin);
    // Dynamic domain list — "Players" is added at the bottom, disabled unless allowed.
    const DOMAINS = [
        ["team", "Teams"],
        ["trainer", "Trainers"],
        ["event", "Events"],
        ["player", "Players"],
    ];
    // Everyone can browse the public SAMPLE athletes; real athletes stay gated server-side.
    useEffect(() => {
    }, [canBrowsePlayers]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Toggle a listing domain (team/trainer/event) from the count buttons — stays in
    // sync with the "All Listings" multiselect since both read/write `domains`.
    const toggleDomain = (d) => setDomains((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));


    // Whether any filter differs from its default — controls the "Clear all" chip.
    const hasActiveFilters = !!(
        search ||
        sport !== DEFAULT_SPORT ||
        domains.length !== 3 || !["team", "trainer", "event"].every((d) => domains.includes(d)) ||
        states.length || ages.length || grades.length || genders.length || circuits.length ||
        nearZip || radius || eventsFrom !== TODAY_ISO || eventsTo
    );

    const clearFilters = () => {
        setSearch("");
        setSport(DEFAULT_SPORT);
        setDomains(["team", "trainer", "event"]);
        setStates([]); setAges([]); setGrades([]); setGenders([]); setCircuits([]); setEventTypes([]);
        setNearZip(""); setRadius(""); setMyLoc(null);
        setEventsFrom(TODAY_ISO); setEventsTo("");
    };

    // Gender must always keep at least one option selected. The only way MultiSelect
    // yields an empty gender set is [NONE_TOKEN]; reject it and tell the user why.
    const handleGenders = (next) => {
        if (next.length === 1 && next[0] === NONE_TOKEN) {
            toast.error("At least one gender must be selected");
            return;
        }
        setGenders(next);
    };

    // Only send near_zip/radius to the API once a full 5-digit ZIP is entered,
    // so partial keystrokes ("3","30",…) don't trigger 400 "Could not locate" errors.
    const validZip = /^\d{5}$/.test(nearZip) ? nearZip : "";

    // Share the current live search — opens the OS/browser share options (Web Share API),
    // falling back to copying the link when sharing isn't available.
    const buildSearchUrl = () => {
        const p = new URLSearchParams();
        if (search) p.set("q", search);
        if (sport) p.set("sport", sport);
        if (domains.length) p.set("domains", domains.join(","));
        if (states.length) p.set("states", states.join(","));
        if (ages.length) p.set("ages", ages.join(","));
        if (grades.length) p.set("grades", grades.join(","));
        if (genders.length) p.set("genders", genders.join(","));
        if (circuits.length) p.set("circuits", circuits.join(","));
        if (eventTypes.length) p.set("types", eventTypes.join(","));
        if (validZip) p.set("zip", validZip);
        if (validZip && radius) p.set("radius", radius);
        return `${window.location.origin}/?${p.toString()}`;
    };
    const shareSearch = async () => {
        const url = buildSearchUrl();
        if (navigator.share) {
            try {
                await navigator.share({ title: "Travel Sports Info — Search", text: "Check out this search on Travel Sports Info", url });
                return;
            } catch (err) {
                if (err && err.name === "AbortError") return; // user cancelled — no fallback
            }
        }
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Search link copied to clipboard");
        } catch {
            window.prompt("Copy this search link:", url);
        }
    };


    // Sync the currently-selected sport into the app-wide SportContext so the
    // Header, Footer and TosModal chrome update their brand/name immediately.
    const { setSport: setContextSport } = useSport();
    useEffect(() => { setContextSport(sport); }, [sport, setContextSport]);

    // Picking a sport updates local state AND the URL so each sport has its own
    // shareable, crawlable path (travelsports.info/basketball, /soccer, …).
    const changeSport = (s) => {
        setSport(s);
        navigate(`/${sportToSlug(s)}`);
    };

    // Keep the URL in sync with the state / circuit filter (so the address bar, tab
    // title and canonical always match what's on screen):
    //   • exactly one state (and no circuit)   -> /sport/state   (e.g. /basketball/texas)
    //   • exactly one circuit (and no state)    -> /sport/circuit (e.g. /basketball/prepgirlshoops)
    //   • anything else                         -> /sport         (e.g. /basketball)
    // The first run (mount) is skipped so arriving on a state/circuit URL — or a returning
    // visitor's saved filter — never triggers a surprise redirect; only user edits sync.
    const stateSyncReady = useRef(false);
    useEffect(() => {
        if (!urlSport) return; // home (/) stays generic
        if (!stateSyncReady.current) { stateSyncReady.current = true; return; }
        const sportSlug = sportToSlug(urlSport);
        let desired = `/${sportSlug}`;
        const realCircuits = circuits.filter((c) => c !== NONE_TOKEN);
        // The circuit splash URL is used only when the page matches the circuit page's
        // shape: exactly one circuit, no state, and only Teams + Events selected (no trainers).
        const onlyTeamsEvents = domains.length === 2 && domains.includes("team") && domains.includes("event");
        if (states.length === 1 && realCircuits.length === 0) {
            const name = ABBR_TO_STATE[states[0]];
            if (name) desired = `/${sportSlug}/${stateSlug(name)}`;
        } else if (realCircuits.length === 1 && states.length === 0 && onlyTeamsEvents) {
            desired = `/${sportSlug}/${circuitSlug(realCircuits[0])}`;
        }
        if (window.location.pathname !== desired) navigate(desired, { replace: true });
    }, [states, circuits, domains, urlSport, navigate]);

    // Browser tab title + all SEO meta are managed by the <Seo> component below.

    const [teams, setTeams] = useState([]);
    const [trainers, setTrainers] = useState([]);
    const [events, setEvents] = useState([]);
    const [players, setPlayers] = useState([]);
    // Per-sport totals (ignoring every other filter). Refetched whenever the sport changes.
    const [totals, setTotals] = useState({ teams: 0, trainers: 0, events: 0 });

    useEffect(() => {
        api.get("/stats/totals", { params: { sport } }).then((r) => setTotals(r.data)).catch(() => {});
    }, [sport]);

    // Measure the hero headline width so the tagline can be bound to it (wraps to
    // fit under the sport name instead of running wider than the header text).
    const heroHeadingRef = useRef(null);
    const [heroDescWidth, setHeroDescWidth] = useState(null);
    useEffect(() => {
        const el = heroHeadingRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;
        const update = () => setHeroDescWidth(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [sport]);

    // User-created / "other" circuits for the active sport — same source as the Add-Team/Event
    // circuit box (CircuitSelect), so the search filter offers the exact same circuit list.
    const [userCircuits, setUserCircuits] = useState([]);
    const [circuitCounts, setCircuitCounts] = useState({});
    useEffect(() => {
        api.get(`/circuits/user?sport=${sport}`).then((r) => setUserCircuits(r.data || [])).catch(() => setUserCircuits([]));
        api.get(`/stats/circuit-counts?sport=${sport}`).then((r) => setCircuitCounts(r.data?.counts || {})).catch(() => setCircuitCounts({}));
    }, [sport]);
    const userCircuitNames = useMemo(() => userCircuits.map((c) => c.name), [userCircuits]);

    // Admin-defined tier layout (drives the grouped, ordered public circuit dropdown).
    const circuitCfg = useCircuitConfig(sport);
    const circuitGroups = useMemo(() => {
        const groups = (circuitCfg.tiers || []).map((t) => ({ label: t.name, items: t.circuits }));
        const unsorted = [...(circuitCfg.unsorted || [])];
        if (!unsorted.some((c) => c.toLowerCase() === "other")) unsorted.push("Other");
        if (unsorted.length) groups.push({ label: groups.length ? "Other circuits" : "Circuits", items: unsorted });
        if ((circuitCfg.other || []).length) groups.push({ label: "Other circuits (added by users)", items: circuitCfg.other });
        return groups;
    }, [circuitCfg]);
    const circuitValid = useMemo(
        () => new Set([...(circuitCfg.all_known || []), ...userCircuitNames, "Other"].map((c) => c)),
        [circuitCfg, userCircuitNames],
    );

    // Persist every filter change to cookie
    useEffect(() => {
        saveFilters({
            search,
            sport,
            domains,
            states,
            ages,
            grades,
            genders,
            circuits,
            near_zip: nearZip,
            radius_miles: radius,
            events_from: eventsFrom,
            events_to: eventsTo,
            view,
        });
    }, [search, sport, domains, states, ages, grades, genders, circuits, nearZip, radius, eventsFrom, eventsTo, view]);

    // If the current sport changed and the ages list no longer matches, prune stale ages
    useEffect(() => {
        const valid = new Set(agesForSport(sport));
        setAges((prev) => prev.filter((a) => valid.has(a)));
    }, [sport]);

    // If selected circuits are no longer valid for the current sport, prune them.
    // Valid = the sport's official circuits + verified/user circuits + "Other".
    // Guard against returning a new array when nothing changed (avoids a render loop).
    useEffect(() => {
        // Don't prune until the circuit config has actually loaded, otherwise a
        // forced-circuit landing page (/basketball/madehoops) loses its selection
        // before "all_known" arrives and gets redirected away.
        if (!circuitCfg.all_known || circuitCfg.all_known.length === 0) return;
        setCircuits((prev) => {
            const next = prev.filter((c) => circuitValid.has(c));
            return next.length === prev.length ? prev : next;
        });
    }, [sport, circuitValid]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch each domain that is selected. Debounced, with a "latest response wins" guard so
    // fast typing / mount re-runs can't let a stale request overwrite the newest results.
    useEffect(() => {
        const csv = (arr) => (arr && arr.length ? arr.join(",") : undefined);
        // defaultAll filter dim → undefined (omit = all) | NONE_TOKEN (none = show nothing) | csv(subset)
        const dim = (arr) => (!arr || arr.length === 0 ? undefined : (arr.length === 1 && arr[0] === NONE_TOKEN) ? NONE_TOKEN : arr.filter((v) => v !== NONE_TOKEN).join(","));
        const timer = setTimeout(() => {
        const myId = ++fetchSeq.current;
        const fresh = () => fetchSeq.current === myId;
        // A team/event dimension explicitly cleared to "none" → those domains show nothing.
        const noneActive = [ages, grades, genders, circuits].some((a) => a.length === 1 && a[0] === NONE_TOKEN);

        // Always fetch all three domains (respecting the other filters) so the count
        // buttons show the true total even when a domain is toggled off; the map/list
        // just hide the deselected types.
        if (noneActive) {
            if (fresh()) setTeams([]);
        } else {
            const params = {};
            if (search) params.search = search;
            if (sport) params.sport = sport;
            if (states.length) params.state = csv(states);
            if (dim(ages) !== undefined) params.age_group = dim(ages);
            if (dim(grades) !== undefined) {
                params.grade = dim(grades);
                const gs = dim(grades).split(",");
                params.grad_year = [...new Set(gs.map((g) => gradeToGradYear(g)))].join(",");
            }
            if (dim(genders) !== undefined) params.gender = dim(genders);
            if (dim(circuits) !== undefined) params.circuit = dim(circuits);
            if (validZip) params.near_zip = validZip;
            if (validZip && radius) params.radius_miles = radius;
            if (myLoc) { params.near_lat = myLoc.lat; params.near_lng = myLoc.lng; }
            api.get("/teams", { params }).then((r) => { if (fresh()) setTeams(r.data); }).catch(() => { if (fresh()) setTeams([]); });
        }

        // Trainers — public trainer LISTINGS only (fake profile accounts were retired).
        {
            const lparams = {};
            if (search) lparams.search = search;
            if (sport) lparams.sport = sport;
            if (validZip) lparams.near_zip = validZip;
            if (validZip && radius) lparams.radius_miles = radius;
            if (myLoc) { lparams.near_lat = myLoc.lat; lparams.near_lng = myLoc.lng; }
            const q = (search || "").trim().toLowerCase();
            const stateSet = new Set(states);
            api.get("/trainer-listings", { params: lparams }).then((r) => r.data).catch(() => []).then((listings) => {
                if (!fresh()) return;
                let tagged = (listings || []).map((l) => ({ ...l, _listing: true }));
                if (stateSet.size) {
                    tagged = tagged.filter((l) => stateSet.has((l.state || "").toUpperCase()));
                }
                if (q) {
                    tagged = tagged.filter((l) =>
                        `${l.name || ""} ${l.city || ""} ${l.state || ""}`.toLowerCase().includes(q)
                    );
                }
                setTrainers(tagged);
                setBooted(true);
            });
        }

        // Events
        if (noneActive) {
            if (fresh()) setEvents([]);
        } else {
            // When the FROM date is set into the past, include past events (bounded by
            // from/to). Otherwise keep the default upcoming-only view.
            const params = { when: eventsFrom && eventsFrom < TODAY_ISO ? "all" : "upcoming" };
            if (search) params.search = search;
            if (sport) params.sport = sport;
            if (states.length) params.state = csv(states);
            if (dim(ages) !== undefined) params.age_group = dim(ages);
            if (dim(grades) !== undefined) {
                params.grade = dim(grades);
                const gs = dim(grades).split(",");
                params.grad_year = [...new Set(gs.map((g) => gradeToGradYear(g)))].join(",");
                const carry = [...new Set(gs.map(gradeOneBelow).filter(Boolean))];
                if (carry.length) params.grade_carry = carry.join(",");
            }
            if (dim(genders) !== undefined) params.gender = dim(genders);
            if (dim(circuits) !== undefined) params.circuit = dim(circuits);
            if (dim(eventTypes) !== undefined) params.type = dim(eventTypes);
            if (validZip) params.near_zip = validZip;
            if (validZip && radius) params.radius_miles = radius;
            if (myLoc) { params.near_lat = myLoc.lat; params.near_lng = myLoc.lng; }
            if (eventsFrom) params.from_date = eventsFrom;
            if (eventsTo) params.to_date = eventsTo;
            api.get("/events", { params }).then((r) => { if (fresh()) setEvents(r.data); }).catch(() => { if (fresh()) setEvents([]); });
        }

        // Players — public sample athletes for everyone; Pro/scout also see real athletes.
        const showPlayers = domains.includes("player");
        if (showPlayers) {
            const params = {};
            if (search) params.search = search;
            if (validZip) params.near_zip = validZip;
            if (validZip && radius) params.radius_miles = radius;
            if (myLoc) { params.near_lat = myLoc.lat; params.near_lng = myLoc.lng; }
            if (athPositions.length) params.position = athPositions.join(",");
            if (athGradYears.length) params.grad_year = athGradYears.join(",");
            if (athHeightMin) params.height_min = athHeightMin;
            if (athAgeMax) params.age_max = athAgeMax;
            api.get("/players", { params }).then((r) => { if (fresh()) setPlayers(r.data); }).catch(() => { if (fresh()) setPlayers([]); });
        } else {
            setPlayers([]);
        }

        }, 250);
        return () => clearTimeout(timer);
    }, [search, sport, domains, states, ages, grades, genders, circuits, eventTypes, nearZip, radius, eventsFrom, eventsTo, canBrowsePlayers, athPositions, athGradYears, athHeightMin, athAgeMax]);

    // Furthest-listing distance from the ZIP — drives the radius slider's dynamic max.
    // Fetched WITHOUT a radius (respecting the other filters) so it stays stable no matter
    // where the slider sits. Uses true positions, so AK/HI listings count.
    const [maxDist, setMaxDist] = useState(null);
    useEffect(() => {
        if (!validZip) { setMaxDist(null); return; }
        const csv = (arr) => (arr && arr.length ? arr.join(",") : undefined);
        const dim = (arr) => (!arr || arr.length === 0 ? undefined : (arr.length === 1 && arr[0] === NONE_TOKEN) ? NONE_TOKEN : arr.filter((v) => v !== NONE_TOKEN).join(","));
        const noneActive = [ages, grades, genders, circuits].some((a) => a.length === 1 && a[0] === NONE_TOKEN);
        const myId = ++maxDistSeq.current;
        const timer = setTimeout(() => {
            const base = { near_zip: validZip };
            if (search) base.search = search;
            if (sport) base.sport = sport;
            if (states.length) base.state = csv(states);
            if (dim(ages) !== undefined) base.age_group = dim(ages);
            if (dim(grades) !== undefined) {
                base.grade = dim(grades);
                base.grad_year = [...new Set(dim(grades).split(",").map((g) => gradeToGradYear(g)))].join(",");
            }
            if (dim(genders) !== undefined) base.gender = dim(genders);
            if (dim(circuits) !== undefined) base.circuit = dim(circuits);
            const eventParams = { ...base, when: "all" };
            if (dim(grades) !== undefined) {
                const carry = [...new Set(dim(grades).split(",").map(gradeOneBelow).filter(Boolean))];
                if (carry.length) eventParams.grade_carry = carry.join(",");
            }
            if (eventsFrom) eventParams.from_date = eventsFrom;
            if (eventsTo) eventParams.to_date = eventsTo;
            const reqs = [
                api.get("/trainer-listings", { params: base }).then((r) => r.data).catch(() => []),
            ];
            reqs.push(noneActive ? Promise.resolve([]) : api.get("/teams", { params: base }).then((r) => r.data).catch(() => []));
            reqs.push(noneActive ? Promise.resolve([]) : api.get("/events", { params: eventParams }).then((r) => r.data).catch(() => []));
            Promise.all(reqs).then((sets) => {
                if (maxDistSeq.current !== myId) return;
                let m = 0;
                sets.flat().forEach((it) => { const d = Number(it.distance_miles); if (!Number.isNaN(d) && d > m) m = d; });
                setMaxDist(m > 0 ? m : null);
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [validZip, myLoc, search, sport, states, ages, grades, genders, circuits, eventsFrom, eventsTo]);  // eslint-disable-line react-hooks/exhaustive-deps


    // Merge everything into one list for the map. Each item gets a `type` so MapView colours it.
    // Team markers are collapsed to one-per-program so the map shows a single pin per club.
    const teamPrograms = useMemo(() => groupIntoPrograms(teams), [teams]);

    // Dynamic radius-slider ceiling: scale up to the furthest listing, with an extra "Unltd"
    // stop at the very end. Falls back to the fixed max until the furthest distance loads.
    const dynMax = useMemo(() => {
        if (!maxDist) return RADIUS_MAX;
        const base = Math.ceil(maxDist / RADIUS_STEP) * RADIUS_STEP;
        return Math.min(RADIUS_HARD_MAX, Math.max(RADIUS_MIN + RADIUS_STEP, base));
    }, [maxDist]);
    const sliderMax = dynMax + RADIUS_STEP; // last tick = Unltd (stored as "")

    // Any filter adjustment that empties every domain shows a red reset banner over the
    // map/list. Gated on `booted` so the initial (pre-fetch) empty state doesn't flash it.
    const noResults = booted && hasActiveFilters && teamPrograms.length === 0 && trainers.length === 0 && events.length === 0;
    const mapMarkers = useMemo(() => {
        const teamMarkers = teamPrograms
            .filter((p) => p.lat != null && p.lng != null)
            .map((p) => ({
                id: p.representative.id,
                slug: p.representative.slug,
                type: "team",
                name: p.base_name,
                city: p.city,
                state: p.state,
                zip: p.zip,
                age_group: [...collapseRanges(p.age_groups, ALL_AGES), ...collapseRanges(p.grades, GRADES), ...(p.grad_years || [])].slice(0, 3).join(" · "),
                division: "",
                lat: p.lat,
                lng: p.lng,
                logo: p.logo,
                preview_bg: p.preview_bg,
                sport: p.sport,
                circuit: p.circuit,
                owner_verified: p.owner_verified,
                avg_rating: p.avg_rating,
                review_count: p.review_count,
                distance_miles: p.representative?.distance_miles ?? p.distance_miles ?? null,
            }));
        const trainerMarkers = trainers.filter((t) => t.home_lat != null && t.home_lng != null).map((t) => ({
            id: t.id,
            type: "trainer",
            name: t.name,
            city: t._listing ? (t.city || "") : (t.home_zip || ""),
            state: t._listing ? (t.state || "") : ((t.specialties && t.specialties[0]) || "Trainer"),
            age_group: t.years_experience != null ? `${t.years_experience}y exp` : "",
            division: (t.specialties || []).slice(0, 2).join(" · "),
            lat: t.home_lat,
            lng: t.home_lng,
            years_experience: t.years_experience,
            specialties: t.specialties,
            logo: t.logo,
            photo: t.photo,
            preview_bg: t.preview_bg,
            avg_rating: t.avg_rating,
            review_count: t.review_count,
            link: t._listing ? `/trainer-listings/${t.id}` : `/trainers/${t.id}`,
            distance_miles: t.distance_miles ?? null,
        }));
        const eventMarkers = events.filter((e) => e.lat != null && e.lng != null).map((e) => ({
            id: e.id,
            link: `/events/${e.detail_event_id || e.slug || e.id}`,
            type: "event",
            name: e.name,
            city: e.city,
            state: e.state,
            age_group: formatDate(e.event_date) + (e.start_time ? ` · ${formatTimeRange(e.start_time, e.end_time)}${tzAbbrev(e.timezone, e.event_date) ? " " + tzAbbrev(e.timezone, e.event_date) : ""}` : ""),
            division: e.type,
            audience: eventAudience(e),
            lat: e.lat,
            lng: e.lng,
            photo: e.photo,
            distance_miles: e.distance_miles ?? null,
        }));
        const playerMarkers = players.filter((p) => p.home_lat != null && p.home_lng != null).map((p) => {
            const ht = p.height_inches != null ? `${Math.floor(p.height_inches / 12)}'${p.height_inches % 12}"` : "";
            return {
                id: p.id,
                type: "player",
                name: p.name,
                photo: p.athlete_photo || "",
                example: !!p.is_example,
                city: [p.home_city, p.position, p.grad_year ? String(p.grad_year) : "", ht].filter(Boolean).join(" · "),
                state: "",
                age_group: "",
                division: "",
                lat: p.home_lat,
                lng: p.home_lng,
                link: `/players/${p.id}`,
                distance_miles: p.distance_miles ?? null,
                in_range: p.distance_miles != null && p.max_travel_distance != null ? p.distance_miles <= p.max_travel_distance : null,
            };
        });
        const showT = domains.length === 0 || domains.includes("team");
        const showTr = domains.length === 0 || domains.includes("trainer");
        const showE = domains.length === 0 || domains.includes("event");
        const showP = domains.includes("player");
        return [...(showT ? teamMarkers : []), ...(showTr ? trainerMarkers : []), ...(showE ? eventMarkers : []), ...(showP ? playerMarkers : [])];
    }, [teamPrograms, trainers, events, players, domains]);

    const activeSportForAdd = sport || DEFAULT_SPORT;
    const defaultAddType = domains.includes("team") ? "team" : domains.includes("trainer") ? "trainer" : "event";

    const goAdd = () => {
        const target = `/add/${encodeURIComponent(activeSportForAdd)}?type=${defaultAddType}`;
        if (!user) navigate(`/login?next=${encodeURIComponent(target)}`);
        else navigate(target);
    };

    const renderViewControls = (pinned = false) => {
        const s = pinned ? "-pinned" : "";
        return (
            <>
                <div className="inline-flex items-stretch border border-[#2a2a2a] shrink-0">
                    <button type="button" data-testid={`view-map${s}`} aria-pressed={view === "map"} onClick={() => setView("map")} className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] inline-flex items-center transition-colors border-r border-[#2a2a2a] ${view === "map" ? "bg-[#ff4500] text-white" : "bg-transparent text-[#a0a0a0] hover:text-white"}`}>Map</button>
                    <button type="button" data-testid={`view-list${s}`} aria-pressed={view === "list"} onClick={() => setView("list")} className={`px-3 h-9 text-[11px] uppercase tracking-[0.18em] inline-flex items-center transition-colors ${view === "list" ? "bg-[#ff4500] text-white" : "bg-transparent text-[#a0a0a0] hover:text-white"}`}>List</button>
                </div>
                <div className="flex items-center gap-2 flex-wrap" data-testid={`map-result-count${s}`}>
                    <CountToggle color="#ff4500" label="teams" icon={<Users className="h-3.5 w-3.5" />} active={domains.includes("team")} testid={`domain-toggle-team${s}`} onClick={() => toggleDomain("team")}>{teamPrograms.length}</CountToggle>
                    <CountToggle color="#2196f3" label="trainers" icon={<Dumbbell className="h-3.5 w-3.5" />} active={domains.includes("trainer")} testid={`domain-toggle-trainer${s}`} onClick={() => toggleDomain("trainer")}>{trainers.length}</CountToggle>
                    <CountToggle color="#22c55e" label="events" icon={<Calendar className="h-3.5 w-3.5" />} active={domains.includes("event")} testid={`domain-toggle-event${s}`} onClick={() => toggleDomain("event")}>{events.length}</CountToggle>
                    {canBrowsePlayers && (
                        <CountToggle color="#a855f7" label="athletes" icon={<User className="h-3.5 w-3.5" />} active={domains.includes("player")} testid={`domain-toggle-player${s}`} onClick={() => toggleDomain("player")}>{players.length}</CountToggle>
                    )}
                </div>
            </>
        );
    };

    // ---- SEO metadata (head only; renders nothing visible) ----
    const _sportName = urlSport || null;
    const _sportSlug = urlSport ? sportToSlug(urlSport) : null;
    let seoTitle, seoDesc, seoCanonical, seoJsonLd;
    if (_sportName && forcedState) {
        seoTitle = `AAU & Travel ${_sportName} Teams in ${forcedStateName}`;
        seoDesc = `Find AAU and travel ${_sportName.toLowerCase()} teams, trainers, tournaments and events in ${forcedStateName}. Browse clubs by age group and circuit on TravelSports.info.`;
        seoCanonical = `/${_sportSlug}/${stateSlug(forcedStateName)}`;
        seoJsonLd = { "@context": "https://schema.org", "@type": "CollectionPage", name: seoTitle, description: seoDesc, about: { "@type": "Thing", name: `${_sportName} in ${forcedStateName}` } };
    } else if (_sportName && forcedCircuit) {
        seoTitle = `${forcedCircuit} ${_sportName}: Teams, Tournaments & Events`;
        seoDesc = `Browse every ${forcedCircuit} ${_sportName.toLowerCase()} team, tournament, showcase and event on TravelSports.info. See ${forcedCircuit} schedules, clubs and upcoming dates nationwide.`;
        seoCanonical = `/${_sportSlug}/${circuitSlug(forcedCircuit)}`;
        seoJsonLd = { "@context": "https://schema.org", "@type": "CollectionPage", name: seoTitle, description: seoDesc, about: { "@type": "Thing", name: `${forcedCircuit} ${_sportName}` } };
    } else if (_sportName) {
        seoTitle = `${_sportName}: AAU & Travel Teams, Trainers & Events`;
        seoDesc = `Browse ${_sportName.toLowerCase()} travel and club teams, trainers and upcoming tournaments across the United States. Filter by state, age group and circuit.`;
        seoCanonical = `/${_sportSlug}`;
        seoJsonLd = { "@context": "https://schema.org", "@type": "CollectionPage", name: seoTitle, description: seoDesc };
    } else {
        seoTitle = "Travel Basketball, AAU, Club Teams, Trainers & Events";
        seoDesc = "Find AAU and travel teams, trainers, tournaments and events for basketball, baseball, soccer and more — nationwide. Search by sport, state, age group and circuit (EYBL, AAU and more) on TravelSports.info.";
        seoCanonical = "/";
        seoJsonLd = null; // Organization + WebSite structured data is site-wide (static in index.html)
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Seo title={seoTitle} description={seoDesc} canonical={seoCanonical} jsonLd={seoJsonLd} />
            {/* HERO */}
            <section className="relative border-b border-[#2a2a2a]">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div
                        key={sport}
                        data-testid="hero-background"
                        className="absolute inset-0 opacity-30 transition-opacity duration-500"
                        style={{
                            backgroundImage: `url("${heroForSport(sport)}")`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[#0a0a0a]" />
                </div>

                <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-5 pb-5 md:pt-8 md:pb-12">
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] md:grid-cols-[auto_minmax(0,1fr)_auto] items-stretch gap-3 sm:gap-4 md:gap-6">
                        {/* LEFT: headline + tagline, column hugs the header text width */}
                        <div className="flex flex-col justify-center md:justify-start min-w-0 max-w-[46vw] md:max-w-md">
                            <h1 ref={heroHeadingRef} data-testid="hero-heading" className="font-display text-3xl sm:text-5xl lg:text-7xl tracking-wide leading-[0.95] text-white w-fit">
                                {(() => {
                                    const words = sport.toUpperCase().split(" ");
                                    if (words.length === 1) {
                                        return <span>{words[0]}</span>;
                                    }
                                    const last = words[words.length - 1];
                                    const rest = words.slice(0, -1).join(" ");
                                    return <>{rest}<br /><span>{last}</span></>;
                                })()}
                            </h1>
                            <p
                                className="mt-2 md:mt-4 text-[11px] leading-tight sm:text-sm md:text-base sm:leading-normal text-[#a0a0a0]"
                                style={heroDescWidth ? { width: heroDescWidth } : undefined}
                            >
                                {circuitPageInfo ? (
                                    <span className="text-white font-semibold" data-testid="circuit-hero-intro">{circuitPageInfo.intro}</span>
                                ) : (
                                    <>Teams, trainers &amp; events — all in one place! Find, add and share. Welcome to the community!</>
                                )}
                            </p>
                            {circuitPageInfo?.logo && (
                                <img
                                    src={circuitPageInfo.logo}
                                    alt={`${forcedCircuit} logo`}
                                    data-testid="circuit-hero-logo"
                                    className="mt-3 md:mt-4 w-auto max-w-[180px] sm:max-w-[220px] md:max-w-[260px] h-auto drop-shadow-lg"
                                />
                            )}
                        </div>

                        {/* SLIDESHOW — fills between the header and the counts; on mobile it
                            bleeds to the right screen edge (cancels the section's right padding). */}
                        <div className="min-w-0 overflow-hidden h-32 sm:h-44 md:h-64 -mr-6 md:mr-0" data-testid="hero-slideshow-wrap">
                            <HeroSlideshow sport={sport} />
                        </div>

                        {/* STATS — desktop, right column, spread top-to-bottom */}
                        <div className="hidden md:flex md:flex-col md:justify-between shrink-0" data-testid="hero-stats">
                            <Stat icon={<Users className="h-4 w-4 text-[#ff4500]" />} value={totals.teams} label="Teams" color="#ff4500" testid="stat-teams" />
                            <Stat icon={<Dumbbell className="h-4 w-4 text-[#2196f3]" />} value={totals.trainers} label="Trainers" color="#2196f3" testid="stat-trainers" />
                            <Stat icon={<Calendar className="h-4 w-4 text-[#22c55e]" />} value={totals.events} label="Events" color="#22c55e" testid="stat-events" />
                        </div>
                    </div>

                    {/* STATS — mobile, below the row (flush segments) */}
                    <div className="flex md:hidden items-center mt-4" data-testid="hero-stats-mobile">
                        <Stat icon={<Users className="h-4 w-4 text-[#ff4500]" />} value={totals.teams} label="Teams" color="#ff4500" testid="stat-teams-m" />
                        <Stat icon={<Dumbbell className="h-4 w-4 text-[#2196f3]" />} value={totals.trainers} label="Trainers" color="#2196f3" testid="stat-trainers-m" />
                        <Stat icon={<Calendar className="h-4 w-4 text-[#22c55e]" />} value={totals.events} label="Events" color="#22c55e" testid="stat-events-m" />
                    </div>

                    {/* SEARCH */}
                    <div className="mt-8">
                        <div className="relative w-full max-w-3xl">
                            <form
                                onSubmit={(e) => e.preventDefault()}
                                className="flex w-full border border-[#2a2a2a] bg-[#141414]/80 backdrop-blur"
                            >
                                <div className="px-4 flex items-center text-[#a0a0a0]">
                                    <Search className="h-4 w-4" />
                                </div>
                                <Input
                                    data-testid="hero-search-input"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onFocus={() => setSearchOpen(true)}
                                    placeholder="Search teams, trainers, and events…"
                                    className="h-14 flex-1 border-0 rounded-none bg-transparent text-base placeholder:text-[#666] focus-visible:ring-0 focus-visible:ring-offset-0"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        data-testid="hero-search-clear"
                                        title="Clear search"
                                        aria-label="Clear search"
                                        onClick={() => { setSearch(""); setSearchOpen(false); }}
                                        className="px-4 flex items-center text-[#a0a0a0] hover:text-[#ff4500] transition-colors"
                                    >
                                        <Eraser className="h-4 w-4" />
                                    </button>
                                )}
                            </form>
                            {searchOpen && search.trim() && (
                                <SearchDropdown
                                    search={search}
                                    sport={sport}
                                    eventsFrom={eventsFrom}
                                    nearZip={nearZip}
                                    myLoc={myLoc}
                                    onClose={() => setSearchOpen(false)}
                                    onViewMap={() => goToResults("map")}
                                    onViewList={() => goToResults("list")}
                                />
                            )}
                        </div>
                    </div>

                    {/* FILTERS — Sport (single-select) first, then multi-selects. Content-width boxes that wrap. Cookie-persisted. */}
                    <div className="mt-4">
                        <div className="flex flex-wrap gap-3">
                            <Select value={sport} onValueChange={changeSport}>
                                <SelectTrigger
                                    data-testid="filter-sport"
                                    className="w-auto h-11 rounded-none bg-[#141414] border-2 border-[#ff4500] hover:border-[#ff5722] text-xs uppercase tracking-wider gap-2 text-[#ff4500]"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#141414] border-[#2a2a2a] rounded-none max-h-80">
                                    {SPORTS.map((s) => (
                                        <SelectItem key={s} value={s}>{s}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <MultiSelect
                                fit
                                testid="filter-domain"
                                label="Listings"
                                value={domains}
                                onChange={setDomains}
                                options={DOMAINS}
                                renderTrailing={(v, checked) => (v === "event" && checked ? (
                                    <button
                                        type="button"
                                        data-testid="event-type-expander"
                                        aria-label="Toggle event type filter"
                                        onClick={() => setTypesOpen((o) => !o)}
                                        className="flex items-center justify-center px-4 py-2 -my-0.5 text-[#a0a0a0] hover:text-[#ff4500]"
                                    >
                                        <ChevronDown className={`h-4 w-4 transition-transform ${typesOpen ? "rotate-180" : ""}`} />
                                    </button>
                                ) : null)}
                                renderSub={(v, checked) => (v === "event" && checked && typesOpen ? (
                                    <EventTypeSub selected={eventTypes} onChange={setEventTypes} />
                                ) : null)}
                            />
                            <MultiSelect
                                fit
                                testid="filter-gender"
                                label="Genders"
                                defaultAll
                                hideSelectAll
                                value={genders}
                                onChange={handleGenders}
                                options={GENDERS}
                            />
                            <MultiSelect
                                fit
                                testid="filter-grade"
                                label="Grades"
                                defaultAll
                                value={grades}
                                onChange={setGrades}
                                options={gradeFilterOptions().map((o) => [o.grade, o.label])}
                            />
                            <MultiSelect
                                fit
                                testid="filter-age"
                                label="Ages"
                                defaultAll
                                value={ages}
                                onChange={setAges}
                                options={agesForSport(sport).map((a) => [a, a])}
                            />
                            <MultiSelect
                                fit
                                testid="filter-state"
                                label="States"
                                icon={MapPin}
                                value={states}
                                onChange={setStates}
                                options={US_STATES}
                            />
                            <CircuitFilter
                                testid="filter-circuit"
                                value={circuits}
                                onChange={setCircuits}
                                groups={circuitGroups}
                                counts={circuitCounts}
                            />
                            <SaveSearchButton
                                compact
                                sizeClass="h-11 w-11"
                                testid="filter-save-search"
                                filters={{ sports: [sport], domains, states, ages, grades, genders, circuits, search, near_zip: nearZip, radius_miles: radius }}
                            />
                            <button
                                type="button"
                                data-testid="filter-copy-search-link"
                                onClick={shareSearch}
                                title="Share this search"
                                aria-label="Share"
                                className="h-11 w-11 border border-[#2a2a2a] bg-[#141414] text-[#a0a0a0] hover:text-[#ff4500] hover:border-[#ff4500] transition-colors inline-flex items-center justify-center shrink-0"
                            >
                                <Share2 className="h-4 w-4" />
                            </button>
                            {hasActiveFilters && (
                                <button
                                    type="button"
                                    data-testid="clear-filters-btn"
                                    onClick={clearFilters}
                                    className="h-11 inline-flex items-center gap-2 px-3 border border-[#2a2a2a] bg-[#141414] text-[#a0a0a0] hover:text-white hover:border-red-500 transition-colors text-xs uppercase tracking-wider"
                                >
                                    <X className="h-3.5 w-3.5" /> Reset all
                                </button>
                            )}
                        </div>
                    </div>

                </div>
            </section>

            {/* RESULTS — Map or List */}
            <section ref={resultsRef} className="max-w-7xl w-full mx-auto px-6 md:px-8 pt-3 pb-8 md:pt-4 md:pb-10">
                <div className="mb-3 flex flex-wrap items-center gap-3">
                    {/* Radius box (left) + Map/List controls (right) on the same row; the whole row scrolls away */}
                    <div className="bg-[#141414] border border-[#2a2a2a] focus-within:border-[#ff4500] transition-colors shrink-0 self-start" data-testid="landing-radius-slider-wrap">
                        <div className="flex items-center h-11">
                            <span className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] pl-3 pr-2 border-r border-[#2a2a2a] py-3">Within</span>
                            <span className="px-2 border-r border-[#2a2a2a] self-stretch flex items-center justify-center min-w-[72px] gap-1">
                                <input
                                    data-testid="landing-radius-input"
                                    value={radius === "" ? "" : radius}
                                    onChange={(e) => {
                                        const v = e.target.value.replace(/\D/g, "");
                                        if (v === "") { setRadius(""); return; }
                                        setRadius(String(Math.min(Number(v), RADIUS_HARD_MAX)));
                                    }}
                                    inputMode="numeric"
                                    placeholder="Unltd"
                                    aria-label="Distance in miles (blank = unlimited)"
                                    className="w-[46px] bg-transparent border-0 p-0 text-center text-xs text-[#ff4500] outline-none focus:ring-0 placeholder:text-[#ff4500]"
                                />
                                <span className="text-[10px] text-[#ff4500] whitespace-nowrap">{radius === "" ? "" : "mi"}</span>
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.15em] text-[#a0a0a0] px-2 border-r border-[#2a2a2a] py-3">miles of</span>
                            <Input
                                data-testid="landing-near-zip"
                                value={nearZip}
                                onChange={(e) => { setNearZip(e.target.value.replace(/\D/g, "").slice(0, 5)); setMyLoc(null); }}
                                inputMode="numeric"
                                placeholder="Enter zip code"
                                pattern="[0-9]{5}"
                                className={`h-full w-[132px] border-0 rounded-none bg-transparent px-2 text-center focus-visible:ring-0 focus-visible:ring-offset-0 ${nearZip ? "text-[#ff4500]" : ""}`}
                            />
                            <button
                                type="button"
                                data-testid="landing-use-location"
                                title="Use my location"
                                aria-label="Use my location"
                                onClick={useMyLocation}
                                disabled={locating}
                                className="h-full px-2.5 border-l border-[#2a2a2a] text-[#a0a0a0] hover:text-[#ff4500] transition-colors flex items-center disabled:opacity-60"
                            >
                                {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
                            </button>
                        </div>
                        <div className="border-t border-[#2a2a2a] px-3 py-2.5 w-full max-w-full">
                            <div className="flex items-center gap-3">
                                <span className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] shrink-0">{RADIUS_MIN}</span>
                                <Slider
                                    data-testid="landing-radius-slider"
                                    min={RADIUS_MIN}
                                    max={sliderMax}
                                    step={RADIUS_STEP}
                                    disabled={!validZip}
                                    value={[radius === "" ? sliderMax : Math.min(Math.max(Number(radius) || RADIUS_MIN, RADIUS_MIN), dynMax)]}
                                    onValueChange={([v]) => setRadius(v >= sliderMax ? "" : String(v))}
                                    trackClassName="h-2 bg-[#0f0f0f] border border-[#2a2a2a]"
                                    rangeClassName="bg-[#ff4500]"
                                    thumbClassName="h-5 w-5 border-2 border-[#ff4500] bg-white shadow-md"
                                    className={`flex-1 ${!validZip ? "opacity-50 pointer-events-none" : ""}`}
                                />
                                <span className="text-[9px] uppercase tracking-[0.2em] text-[#a0a0a0] shrink-0">Unltd</span>
                            </div>
                        </div>
                    </div>
                    <div className={`flex items-center gap-2 flex-wrap transition-opacity duration-150 ${stuck ? "invisible opacity-0" : "opacity-100"}`} data-testid="inflow-view-controls">
                        {renderViewControls(false)}
                    </div>
                </div>
                <div ref={stickySentinelRef} aria-hidden="true" className="h-0" data-testid="sticky-sentinel" />
                <div data-testid="list-sticky-bar" className={`fixed left-0 right-0 z-[1000] bg-[#0a0a0a] border-b border-[#1c1c1c] transition-all duration-200 ${stuck ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"}`} style={{ top: "var(--tsi-sticky-top, 64px)" }}>
                    <div data-testid="list-sticky-bar-inner" className="max-w-7xl w-full mx-auto px-6 md:px-8 py-2 flex items-center gap-2 flex-wrap">
                        {renderViewControls(true)}
                    </div>
                </div>
                {canBrowsePlayers && domains.includes("player") && (
                    <div className="mb-3 flex flex-wrap items-center gap-2 border border-[#a855f7]/40 bg-[#a855f7]/5 p-2" data-testid="athlete-filter-bar">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#a855f7] font-semibold">Athlete filters</span>
                        <MultiSelect testid="ath-positions" label="Positions" placeholder="Positions" fit compact value={athPositions} onChange={setAthPositions} options={[["PG", "PG"], ["SG", "SG"], ["SF", "SF"], ["PF", "PF"], ["C", "C"]]} />
                        <MultiSelect testid="ath-gradyears" label="Grad Years" placeholder="Grad Years" fit compact value={athGradYears} onChange={setAthGradYears} options={[2026, 2027, 2028, 2029, 2030, 2031, 2032].map((y) => [String(y), String(y)])} />
                        <select data-testid="ath-height-min" value={athHeightMin} onChange={(e) => setAthHeightMin(e.target.value)} className="h-10 px-2 text-xs bg-[#141414] border border-[#2a2a2a] text-white rounded-none">
                            <option value="">Height</option>
                            {Array.from({ length: 42 }, (_, i) => 48 + i).map((inch) => (
                                <option key={inch} value={inch}>{`${Math.floor(inch / 12)}'${inch % 12}"`}+</option>
                            ))}
                        </select>
                        <select data-testid="ath-age-max" value={athAgeMax} onChange={(e) => setAthAgeMax(e.target.value)} className="h-10 px-2 text-xs bg-[#141414] border border-[#2a2a2a] text-white rounded-none">
                            <option value="">Max age</option>
                            {Array.from({ length: 7 }, (_, i) => 13 + i).map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                        {(athPositions.length || athGradYears.length || athHeightMin || athAgeMax) ? (
                            <button type="button" data-testid="ath-clear" onClick={() => { setAthPositions([]); setAthGradYears([]); setAthHeightMin(""); setAthAgeMax(""); }} className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-white">Clear</button>
                        ) : null}
                        <span className="text-[10px] text-[#a0a0a0]">Enter a ZIP above to sort athletes by distance.</span>
                    </div>
                )}
                {noResults && (
                    <div data-testid="no-results-popup" className="mb-3 flex flex-wrap items-center justify-center gap-3 border border-red-500 bg-red-500/10 px-4 py-3 text-center">
                        <span className="text-sm font-medium text-red-400">No results due to current filter settings.</span>
                        <button
                            type="button"
                            data-testid="no-results-reset-all"
                            onClick={clearFilters}
                            className="inline-flex items-center gap-1.5 px-3 h-8 border border-red-500 bg-red-500 text-white hover:bg-red-600 transition-colors text-xs uppercase tracking-wider"
                        >
                            <X className="h-3.5 w-3.5" /> Reset all
                        </button>
                    </div>
                )}
                {view === "map" ? (
                    <MapView teams={mapMarkers} height="560px" centerZip={nearZip} radiusMiles={radius === "" ? null : Number(radius)} preciseCenter={myLoc} onRequestLocation={useMyLocation} fitToMarkers={states.length === 1 && !validZip} />
                ) : (
                    <ResultsList
                        teams={teams}
                        trainers={trainers}
                        events={events}
                        players={players}
                        domains={domains}
                        hasZip={!!validZip}
                        onAddEvent={goAdd}
                        eventsFrom={eventsFrom}
                        eventsTo={eventsTo}
                        setEventsFrom={setEventsFrom}
                        setEventsTo={setEventsTo}
                        todayIso={TODAY_ISO}
                        eventTypes={eventTypes}
                        setEventTypes={setEventTypes}
                    />
                )}
            </section>

            {/* TRENDING — compact strip of trending board posts (logged-in only) */}
            {user && (
                <section className="max-w-7xl w-full mx-auto px-6 md:px-8 pb-6" data-testid="landing-feed-section">
                    <TrendingStrip compact />
                    <div className="mt-3">
                        <Link to="/feed" data-testid="landing-feed-viewall" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#ff4500] hover:underline">
                            View full feed
                            <MessageSquare className="h-3.5 w-3.5" />
                            {feedBadgeTotal > 0 && (
                                <span data-testid="landing-feed-badge" className="min-w-[18px] h-[18px] px-1.5 bg-[#ff4500] text-black text-[10px] font-extrabold rounded-full inline-flex items-center justify-center no-underline">
                                    {feedBadgeTotal > 99 ? "99+" : feedBadgeTotal}
                                </span>
                            )}
                        </Link>
                    </div>
                </section>
            )}

            {/* EVENTS LIST — shown only in map view when Events domain is selected. Mirrors the list-view events section exactly. */}
            {view === "map" && (domains.length === 0 || domains.includes("event")) && (
                <section className="max-w-7xl w-full mx-auto px-6 md:px-8 pb-6" data-testid="events-list-section">
                    <EventsListSection events={events} hasZip={!!validZip} goAdd={goAdd} eventsFrom={eventsFrom} eventsTo={eventsTo} setEventsFrom={setEventsFrom} setEventsTo={setEventsTo} todayIso={TODAY_ISO} eventTypes={eventTypes} setEventTypes={setEventTypes} />
                </section>
            )}

        </div>
    );
}

// Collapse a set of ages/grades into contiguous ranges using their canonical order,
// e.g. ["10U".."17U"] -> "10U-17U", ["5th","6th","8th"] -> "5th-6th, 8th".
function summarizeRuns(values, order) {
    const idx = (values || []).map((v) => order.indexOf(v)).filter((i) => i >= 0).sort((a, b) => a - b);
    if (!idx.length) return "";
    const runs = [];
    let start = idx[0], prev = idx[0];
    for (let k = 1; k < idx.length; k++) {
        if (idx[k] === prev + 1) { prev = idx[k]; continue; }
        runs.push([start, prev]); start = idx[k]; prev = idx[k];
    }
    runs.push([start, prev]);
    return runs.map(([a, b]) => (a === b ? order[a] : `${order[a]}-${order[b]}`)).join(", ");
}

// Gender (B/G) + collapsed age/grade summary for an event, e.g. "G · 10U-17U".
function eventAudience(e) {
    const genders = e.genders || [];
    const g = [genders.includes("M") ? "B" : null, genders.includes("F") ? "G" : null].filter(Boolean).join("/");
    const grades = summarizeRuns(e.grades, GRADES);
    const ages = summarizeRuns(e.age_groups, agesForSport(e.sport));
    const gradYears = collapseYearRanges(e.grad_years).join(", ");
    const ag = [grades, ages, gradYears].filter(Boolean).join(", ");
    return [g, ag].filter(Boolean).join(" · ");
}

// Days remaining until a cancelled event auto-deletes (cancelled_at + 7 days).
function daysUntilAutoDelete(cancelledAt) {
    if (!cancelledAt) return null;
    const del = new Date(cancelledAt).getTime() + 7 * 24 * 3600 * 1000;
    const ms = del - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / (24 * 3600 * 1000));
}

// Red "CANCELLED" chip + auto-delete note shown under a cancelled event card.
function CancelledMeta({ e }) {
    const d = daysUntilAutoDelete(e.cancelled_at);
    return (
        <div className="mt-1 flex items-center gap-2 flex-wrap">
            <span className="inline-block text-[9px] leading-none tracking-[0.18em] uppercase font-bold px-1.5 py-1 border border-red-500 text-red-500 bg-red-500/10" data-testid={`event-cancelled-chip-${e.id}`}>Cancelled</span>
            <span className="text-[10px] text-[#a0a0a0]" data-testid={`event-cancelled-autodelete-${e.id}`}>Auto-deletes in {d} day{d === 1 ? "" : "s"}</span>
        </div>
    );
}

// Selectable listing-count button (Style 1): grey border when off; border, text and a
// faint tint switch to the listing color when active. Tied to the `domains` filter.
function CountToggle({ color, active, onClick, testid, icon, label, children }) {
    return (
        <button
            type="button"
            data-testid={testid}
            aria-pressed={active}
            onClick={onClick}
            title={active ? `Deselect ${label}` : `View ${label}`}
            style={{
                borderColor: active ? color : "#2a2a2a",
                color: active ? color : "#a0a0a0",
                backgroundColor: active ? `${color}1f` : "#141414",
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs uppercase tracking-[0.2em] transition-colors hover:border-[color:currentColor]"
        >
            <span className="inline-flex items-center justify-center" style={{ color, opacity: active ? 1 : 0.6 }}>{icon}</span>
            {children}
        </button>
    );
}


function Stat({ icon, value, label, color, testid }) {
    return (
        <>
            {/* Mobile: ultra-compact colored strip segment */}
            <span data-testid={testid} className="md:hidden inline-flex items-center gap-1.5 px-3 first:pl-0 border-l border-[#2a2a2a] first:border-l-0">
                {icon}
                <span className="font-display text-2xl leading-none" style={{ color }}>{value}</span>
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color }}>{label}</span>
            </span>
            {/* Desktop: original stacked card */}
            <div className="hidden md:block border-l border-[#2a2a2a] pl-4" data-testid={`${testid}-desktop`}>
                <div className="flex items-center gap-1.5">{icon}</div>
                <div className="font-display text-4xl text-white leading-none mt-1">{value}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] mt-1">{label}</div>
            </div>
        </>
    );
}

// Shared result cards — used identically by the list results and the search dropdown.
export function TeamCard({ p, testid, onNav }) {
    const ageChips = [...collapseRanges(p.age_groups, ALL_AGES), ...collapseRanges(p.grades, GRADES), ...(p.grad_years || [])].slice(0, 4);
    const gender = p.genders.includes("M") && p.genders.includes("F") ? "Boys · Girls" : p.genders.includes("F") ? "Girls" : p.genders.includes("M") ? "Boys" : "";
    const segs = [
        [p.city, p.state].filter(Boolean).join(", ") || null,
        gender || null,
        ageChips.length ? ageChips.join(" · ") : null,
        p.circuit ? <span key="circuit" className="text-[#ff4500]">{p.circuit}</span> : null,
    ].filter(Boolean);
    return (
        <RowLink to={`/teams/${p.representative.slug || p.representative.id}`} testid={testid || `program-card-${p.key}`} color="#ff4500" onClick={onNav}>
            <ThumbCol src={p.logo} fallback={(p.base_name || "?")[0]} color="#ff4500" distance={p.distance_miles} bg={p.preview_bg} fit="contain" />
            <div className="min-w-0 flex-1">
                <div className="font-display text-xl tracking-wide truncate group-hover:text-[#ff4500] flex items-center gap-1.5">
                    {p.base_name}
                    {p.owner_verified && <BadgeCheck className="h-4 w-4 text-[#22c55e] shrink-0" strokeWidth={2.5} data-testid={`verified-team-${p.key}`} />}
                </div>
                <div className="text-xs text-[#a0a0a0] truncate">
                    {segs.reduce((acc, s, i) => (i === 0 ? [s] : [...acc, " · ", s]), [])}
                </div>
                {p.review_count > 0 && <StarRating rating={p.avg_rating} count={p.review_count} />}
            </div>
        </RowLink>
    );
}

export function TrainerCard({ tr, testid, onNav }) {
    return (
        <RowLink to={tr._listing ? `/trainer-listings/${tr.id}` : `/trainers/${tr.id}`} testid={testid || `trainer-card-${tr.id}`} color="#2196f3" onClick={onNav}>
            <ThumbCol src={tr.logo || tr.photo} fallback={(tr.name || "?")[0]} color="#2196f3" distance={tr.distance_miles} bg={tr.preview_bg} fit="contain" />
            <div className="min-w-0 flex-1">
                <div className="font-display text-xl tracking-wide truncate group-hover:text-[#2196f3] flex items-center gap-1.5">
                    {tr.name}
                    {tr.owner_verified && <BadgeCheck className="h-4 w-4 text-[#22c55e] shrink-0" strokeWidth={2.5} data-testid={`verified-trainer-${tr.id}`} />}
                </div>
                <div className="text-xs text-[#a0a0a0] truncate">
                    Trainer{tr.sport ? ` · ${tr.sport}` : ""} · {tr._listing ? ([tr.city, tr.state].filter(Boolean).join(", ") || "Community listing") : ((tr.specialties || []).slice(0, 3).join(" · ") || "Personal Trainer")}
                </div>
                {tr.review_count > 0 && <StarRating rating={tr.avg_rating} count={tr.review_count} />}
            </div>
        </RowLink>
    );
}

export function EventCard({ e, testid, onNav }) {
    const evPast = (e.end_date || e.event_date || "") < todayLocalISO();
    const canc = !!e.cancelled_at;
    return (
        <RowLink to={`/events/${e.detail_event_id || e.slug || e.id}`} testid={testid || `event-card-${e.id}`} color="#22c55e" onClick={onNav}>
            <ThumbCol src={e.photo} fallback={(e.name || "?")[0]} color={canc ? "#ef4444" : "#22c55e"} distance={e.distance_miles} bg="black" fit="contain" />
            <div className="min-w-0 flex-1">
                <div className="min-w-0">
                    <span className={`font-display text-xl tracking-wide break-words ${canc ? "text-red-500" : "group-hover:text-[#22c55e]"}`}>{e.name}</span>
                    {e.owner_verified && <span className="inline-flex align-middle ml-1.5"><BadgeCheck className="h-4 w-4 text-[#22c55e] shrink-0" strokeWidth={2.5} data-testid={`verified-event-${e.id}`} /></span>}
                    {canc && <span className="text-red-500 font-sans text-xs normal-case tracking-normal ml-2" data-testid={`event-cancelled-label-${e.id}`}>(Cancelled?)</span>}
                </div>
                <div className="text-xs"><span className={(canc || evPast) ? "text-red-500" : "text-[#a0a0a0]"}>{formatDateRange(e.event_date, e.end_date)}{e.start_time ? ` · ${formatTimeRange(e.start_time, e.end_time)}${tzAbbrev(e.timezone, e.event_date) ? " " + tzAbbrev(e.timezone, e.event_date) : ""}` : ""}</span></div>
                {(eventAudienceParts(e).length > 0 || e.city || e.state) && <div className="text-xs text-[#a0a0a0] truncate"><EventAudience e={e} />{eventAudienceParts(e).length > 0 && (e.city || e.state) ? " · " : null}{[e.city, e.state].filter(Boolean).join(", ")}</div>}
                {e.review_count > 0 && <StarRating rating={e.avg_rating} count={e.review_count} />}
                {canc && <CancelledMeta e={e} />}
            </div>
        </RowLink>
    );
}


function ResultsList({ teams, trainers, events, players, domains, hasZip, onAddEvent, eventsFrom, eventsTo, setEventsFrom, setEventsTo, todayIso, eventTypes, setEventTypes }) {
    const showTeams = domains.length === 0 || domains.includes("team");
    const showTrainers = domains.length === 0 || domains.includes("trainer");
    const showEvents = domains.length === 0 || domains.includes("event");
    const showPlayers = domains.includes("player");
    const programs = showTeams ? groupIntoPrograms(teams) : [];
    const total = programs.length + trainers.length + events.length + players.length;
    const [teamCtrl, setTeamCtrl] = usePersistedCtrl("teams", { sort: "name", dir: "asc", pageSize: 5, page: 0 });
    const [trainerCtrl, setTrainerCtrl] = usePersistedCtrl("trainers", { sort: "name", dir: "asc", pageSize: 5, page: 0 });
    const [eventCtrl, setEventCtrl] = usePersistedCtrl("events", { sort: "date", dir: "asc", pageSize: 5, page: 0 });
    const [playerCtrl, setPlayerCtrl] = usePersistedCtrl("players", { sort: "name", dir: "asc", pageSize: 5, page: 0 });
    const paged = (items, c) => sortItems(items, c.sort, c.dir).slice(c.page * c.pageSize, c.page * c.pageSize + c.pageSize);
    const dist = (fields) => (hasZip ? fields : fields.filter(([v]) => v !== "distance"));
    // Events "To" filter stays compact (just a label + calendar) until the user opens it.
    const [toOpen, setToOpen] = useState(false);

    // Teams, trainers & athletes default to nearest-first (Distance) sort whenever a
    // ZIP/location is active — on first load AND when a ZIP is entered. Events keep their
    // Date default. Clearing the ZIP reverts each list to its natural default. The visitor
    // can still pick another sort afterward (this only fires when the ZIP state changes).
    const prevZip = useRef(null);
    useEffect(() => {
        const first = prevZip.current === null;
        const gainedZip = hasZip && !prevZip.current;
        const lostZip = !hasZip && prevZip.current;
        if (hasZip && (first || gainedZip)) {
            setTeamCtrl((c) => ({ ...c, sort: "distance", dir: "asc", page: 0 }));
            setTrainerCtrl((c) => ({ ...c, sort: "distance", dir: "asc", page: 0 }));
            setPlayerCtrl((c) => ({ ...c, sort: "distance", dir: "asc", page: 0 }));
        } else if (lostZip) {
            setTeamCtrl((c) => ({ ...c, sort: "name", page: 0 }));
            setTrainerCtrl((c) => ({ ...c, sort: "name", page: 0 }));
            setEventCtrl((c) => ({ ...c, sort: "date", page: 0 }));
            setPlayerCtrl((c) => ({ ...c, sort: "name", page: 0 }));
        }
        prevZip.current = hasZip;
    }, [hasZip, setTeamCtrl, setTrainerCtrl, setEventCtrl, setPlayerCtrl]);

    if (total === 0) {
        return (
            <div className="border border-[#2a2a2a] py-16 text-center" data-testid="results-empty">
                <div className="font-display text-3xl mb-2">No results match your filters</div>
                <div className="text-sm text-[#a0a0a0]">Try broadening your filters or clearing a few.</div>
            </div>
        );
    }

    // Placement 1: one in-list banner ad, shown at the end of the FIRST list rendered.
    const firstList = (showTeams && programs.length > 0) ? "teams"
        : (showTrainers && trainers.length > 0) ? "trainers"
        : (showEvents && events.length > 0) ? "events"
        : (showPlayers && players.length > 0) ? "players"
        : null;

    return (
        <div className="space-y-10" data-testid="results-list">
            {showTeams && programs.length > 0 && (
                <div>
                    <SectionHeader label="Teams" color="#ff4500" icon={Users}>
                        <ListControls testid="teams-controls" ctrl={teamCtrl} setCtrl={setTeamCtrl} fields={dist([["distance", "Distance"], ["rating", "Rating"], ["name", "Name"]])} className="flex flex-wrap items-center gap-2 ml-auto" />
                    </SectionHeader>
                    <div className="grid grid-cols-1 gap-3" data-testid="results-teams-grid">
                        {paged(programs, teamCtrl).map((p) => <TeamCard key={p.key} p={p} />)}
                    </div>
                    <Pager testid="teams-pager" ctrl={teamCtrl} setCtrl={setTeamCtrl} total={programs.length} />
                    {firstList === "teams" && <InListAd />}
                </div>
            )}
            {showTrainers && trainers.length > 0 && (
                <div>
                    <SectionHeader label="Trainers" color="#2196f3" icon={Dumbbell}>
                        <ListControls testid="trainers-controls" ctrl={trainerCtrl} setCtrl={setTrainerCtrl} fields={dist([["distance", "Distance"], ["rating", "Rating"], ["name", "Name"]])} className="flex flex-wrap items-center gap-2 ml-auto" />
                    </SectionHeader>
                    <div className="grid grid-cols-1 gap-3" data-testid="results-trainers-grid">
                        {paged(trainers, trainerCtrl).map((tr) => <TrainerCard key={tr._listing ? `l-${tr.id}` : tr.id} tr={tr} />)}
                    </div>
                    <Pager testid="trainers-pager" ctrl={trainerCtrl} setCtrl={setTrainerCtrl} total={trainers.length} />
                    {firstList === "trainers" && <InListAd />}
                </div>
            )}
            {showEvents && (
                <div>
                    <SectionHeader label="Events" color="#22c55e" icon={Calendar}>
                        <div className="flex flex-wrap items-center gap-2 order-3 md:order-2 basis-full md:basis-auto" data-testid="events-date-range">
                            <div className="flex items-stretch border border-[#2a2a2a] bg-[#141414]/80">
                                <label htmlFor="events-from" className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] px-2.5 flex items-center border-r border-[#2a2a2a] bg-[#0f0f0f] cursor-pointer">From</label>
                                <input id="events-from" type="date" data-testid="events-from-input" value={eventsFrom} max={eventsTo || undefined}
                                    onChange={(e) => setEventsFrom(e.target.value)}
                                    className={`h-9 bg-transparent text-sm px-2 focus-visible:outline-none ${eventsFrom && eventsFrom !== todayIso ? "text-[#ff4500] cal-orange" : "text-white cal-gray"}`} />
                                <button type="button" data-testid="events-from-reset" title="Reset to today" onClick={() => setEventsFrom(todayIso)}
                                    className="px-2 flex items-center text-[#a0a0a0] hover:text-[#ff4500] border-l border-[#2a2a2a]"><Eraser className="h-3.5 w-3.5" /></button>
                            </div>
                            {(toOpen || eventsTo) ? (
                                <div className="flex items-stretch border border-[#2a2a2a] bg-[#141414]/80">
                                    <label htmlFor="events-to" className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] px-2.5 flex items-center border-r border-[#2a2a2a] bg-[#0f0f0f] cursor-pointer">To</label>
                                    <input id="events-to" type="date" data-testid="events-to-input" value={eventsTo} min={eventsFrom || undefined} autoFocus={toOpen && !eventsTo}
                                        onChange={(e) => setEventsTo(e.target.value)}
                                        className={`h-9 bg-transparent text-sm px-2 focus-visible:outline-none ${eventsTo ? "text-[#ff4500] cal-orange" : "text-white cal-gray"}`} />
                                    <button type="button" data-testid="events-to-reset" title="Clear — show all upcoming" onClick={() => { setEventsTo(""); setToOpen(false); }}
                                        className="px-2 flex items-center text-[#a0a0a0] hover:text-[#ff4500] border-l border-[#2a2a2a]"><Eraser className="h-3.5 w-3.5" /></button>
                                </div>
                            ) : (
                                <button type="button" data-testid="events-to-open" title="Set an end date" onClick={() => setToOpen(true)}
                                    className="h-9 inline-flex items-center gap-1.5 border border-[#2a2a2a] bg-[#141414]/80 px-2.5 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] hover:border-[#3a3a3a]">
                                    To <Calendar className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                        {events.length > 0 && <ListControls testid="events-controls" ctrl={eventCtrl} setCtrl={setEventCtrl} fields={dist([["date", "Date"], ["distance", "Distance"], ["name", "Name"]])} leading={<EventTypeDropdown selected={eventTypes} onChange={setEventTypes} />} className="flex flex-wrap items-center gap-2 ml-auto order-2 md:order-3" />}
                    </SectionHeader>
                    {events.length === 0 ? (
                        <div className="border border-[#2a2a2a] py-8 text-center" data-testid="results-events-empty">
                            <div className="text-sm text-[#a0a0a0]">No events match. <button type="button" onClick={onAddEvent} className="text-[#22c55e] hover:underline uppercase tracking-wider text-xs">Add an event</button></div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-3" data-testid="results-events-grid">
                                {paged(events, eventCtrl).map((e) => <EventCard key={e.id} e={e} />)}
                            </div>
                            <Pager testid="events-pager" ctrl={eventCtrl} setCtrl={setEventCtrl} total={events.length} />
                            {firstList === "events" && <InListAd />}
                        </>
                    )}
                </div>
            )}
            {showPlayers && (
                <div>
                    <SectionHeader label="Players" color="#a855f7" icon={User}>
                        {players.length > 0 && <ListControls testid="players-controls" ctrl={playerCtrl} setCtrl={setPlayerCtrl} fields={dist([["distance", "Distance"], ["name", "Name"]])} className="flex flex-wrap items-center gap-2 ml-auto" />}
                    </SectionHeader>
                    {players.length === 0 ? (
                        <div className="border border-[#2a2a2a] py-8 text-center" data-testid="results-players-empty"><div className="text-sm text-[#a0a0a0]">No players match your current filters.</div></div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 gap-3" data-testid="results-players-grid">
                                {paged(players, playerCtrl).map((p) => (
                                    <RowLink key={p.id} to={`/players/${p.id}`} testid={`player-card-${p.id}`} color="#a855f7">
                                        <ThumbCol src={p.athlete_photo || p.photo} fallback={(p.name || "?")[0]} color="#a855f7" distance={hasZip ? p.distance_miles : null} outOfRange={hasZip && p.distance_miles != null && p.max_travel_distance != null ? p.distance_miles > p.max_travel_distance : null} />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <div className="font-display text-xl tracking-wide truncate group-hover:text-[#a855f7]">{p.name}</div>
                                                {p.is_example && <span className="shrink-0 bg-[#ff4500] text-white text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-sm">Example</span>}
                                            </div>
                                            <div className="text-xs text-[#a0a0a0] truncate">{[p.home_city && p.home_state ? `${p.home_city}, ${p.home_state}` : "", p.position || "", p.grad_year ? `Class of ${p.grad_year}` : "", p.age ? `Age ${p.age}` : ""].filter(Boolean).join(" · ")}</div>
                                            {p.bio && <div className="text-xs text-[#8a8a8a] truncate mt-0.5">{p.bio}</div>}
                                        </div>
                                    </RowLink>
                                ))}
                            </div>
                            <Pager testid="players-pager" ctrl={playerCtrl} setCtrl={setPlayerCtrl} total={players.length} />
                            {firstList === "players" && <InListAd />}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function SectionHeader({ label, color, icon: Icon, children, stickyTop = "var(--tsi-list-top, 112px)" }) {
    return (
        <div className="sticky z-30 -mx-6 md:-mx-8 px-6 md:px-8 py-2 bg-[#0a0a0a] flex flex-wrap items-center gap-x-4 gap-y-2 mb-3" style={{ top: stickyTop }}>
            <div className="flex items-center gap-2 shrink-0">
                {Icon ? <Icon className="h-5 w-5 self-center" style={{ color }} /> : <span className="h-2.5 w-2.5" style={{ background: color }} />}
                <h3 className="font-display text-2xl tracking-wide" style={{ color }}>{label}</h3>
            </div>
            {children}
        </div>
    );
}

// ---- Single-column list helpers: sorting, page-size, pagination ----
function sortItems(items, field, dir) {
    const val = (x) => {
        if (field === "name") return (x.name || x.base_name || "").toLowerCase();
        if (field === "rating") return x.avg_rating ?? x.review_rating ?? x.rating ?? 0;
        if (field === "distance") return x.distance_miles ?? Infinity;
        if (field === "date") return x.event_date || "";
        return 0;
    };
    const s = [...items].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0));
    return dir === "desc" ? s.reverse() : s;
}

function usePersistedCtrl(key, defaults) {
    const [ctrl, setCtrl] = useState(() => {
        const saved = getListPrefs({})[key] || {};
        return { ...defaults, ...saved, page: 0 };
    });
    useEffect(() => {
        const all = getListPrefs({});
        all[key] = { sort: ctrl.sort, dir: ctrl.dir, pageSize: ctrl.pageSize };
        saveListPrefs(all);
    }, [key, ctrl.sort, ctrl.dir, ctrl.pageSize]);
    return [ctrl, setCtrl];
}

// Event-type filter dropdown for the list-view toolbars (left of Sort). Neutral
// "All Types" until a type is picked; shares state with the main multiselect sub-filter.
function EventTypeDropdown({ selected, onChange }) {
    return (
        <MultiSelect
            fit size="xs" defaultAll
            testid="events-type-filter"
            label="All"
            icon={Calendar}
            options={EVENT_TYPE_OPTIONS.map((t) => [t, t])}
            value={selected}
            onChange={onChange}
        />
    );
}

// Expanded "Type" list shown under the checked "Events" row (toggled by the Events-row
// chevron). First row is Select All (checked = all types = no filter); deselect it to
// pick specific types. Shares state with the list-view "All" dropdown.
function EventTypeSub({ selected, onChange }) {
    const real = selected.filter((t) => t !== NONE_TOKEN);
    const showAll = selected.length === 0;
    const allSelected = showAll || EVENT_TYPE_OPTIONS.every((t) => real.includes(t));
    const on = (t) => (showAll ? true : real.includes(t));
    const toggle = (t) => {
        const base = showAll ? [...EVENT_TYPE_OPTIONS] : real;
        const next = base.includes(t) ? base.filter((x) => x !== t) : [...base, t];
        if (next.length === EVENT_TYPE_OPTIONS.length) { onChange([]); return; }
        if (next.length === 0) { onChange([NONE_TOKEN]); return; }
        onChange(next);
    };
    const Row = ({ label, checked, onClick, testid, head = false }) => (
        <button
            type="button"
            data-testid={testid}
            onClick={onClick}
            className={`w-full flex items-center gap-2 pl-6 pr-4 py-1.5 text-left whitespace-nowrap hover:bg-[#1a1a1a] ${head ? "text-[11px] uppercase tracking-wider text-white" : "text-[12px] text-white"}`}
        >
            <span className={`h-3.5 w-3.5 border flex items-center justify-center shrink-0 ${checked ? "bg-[#ff4500] border-[#ff4500]" : "border-[#3a3a3a]"}`}>
                {checked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
            </span>
            {label}
        </button>
    );
    return (
        <div className="bg-[#0f0f0f] border-b border-[#2a2a2a]">
            <Row label="Select All" checked={allSelected} onClick={() => onChange(allSelected ? [NONE_TOKEN] : [])} testid="event-type-select-all" head />
            {EVENT_TYPE_OPTIONS.map((t) => (
                <Row key={t} label={t} checked={on(t)} onClick={() => toggle(t)} testid={`event-type-opt-${t}`} />
            ))}
        </div>
    );
}

function ListControls({ ctrl, setCtrl, fields, testid, className = "flex flex-wrap items-center gap-2 mb-3", leading = null }) {
    const mobile = useIsMobile();
    // If the active sort field is no longer offered (e.g. Distance hidden when no ZIP), fall back to the first field.
    useEffect(() => {
        if (!fields.some(([v]) => v === ctrl.sort)) {
            setCtrl((c) => ({ ...c, sort: fields[0][0], page: 0 }));
        }
    }, [fields, ctrl.sort, setCtrl]);
    return (
        <div className={className} data-testid={testid}>
            {leading}
            <select data-testid={`${testid}-sort`} value={ctrl.sort} onChange={(e) => setCtrl((c) => ({ ...c, sort: e.target.value, page: 0 }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                {fields.map(([v, l]) => <option key={v} value={v}>{mobile ? l : `Sort: ${l}`}</option>)}
            </select>
            <button type="button" data-testid={`${testid}-dir`} onClick={() => setCtrl((c) => ({ ...c, dir: c.dir === "asc" ? "desc" : "asc" }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 uppercase tracking-wider">
                {ctrl.dir === "asc" ? "▲ Asc" : "▼ Desc"}
            </button>
            <select data-testid={`${testid}-size`} value={ctrl.pageSize} onChange={(e) => setCtrl((c) => ({ ...c, pageSize: Number(e.target.value), page: 0 }))} className="bg-[#141414] border border-[#2a2a2a] text-white text-xs px-2 py-1.5 rounded-none">
                {[5, 10, 25, 50].map((n) => <option key={n} value={n}>{mobile ? `${n} / pg` : `${n} / page`}</option>)}
            </select>
        </div>
    );
}

function Pager({ ctrl, setCtrl, total, testid }) {
    const pages = Math.ceil(total / ctrl.pageSize);
    if (pages <= 1) return null;
    return (
        <div className="flex items-center justify-center gap-3 mt-4" data-testid={testid}>
            <button type="button" disabled={ctrl.page <= 0} onClick={() => setCtrl((c) => ({ ...c, page: c.page - 1 }))} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Prev</button>
            <span className="text-xs text-[#a0a0a0]">Page {ctrl.page + 1} / {pages}</span>
            <button type="button" disabled={ctrl.page >= pages - 1} onClick={() => setCtrl((c) => ({ ...c, page: c.page + 1 }))} className="px-3 py-1.5 text-xs uppercase tracking-wider border border-[#2a2a2a] disabled:opacity-40 hover:border-white">Next</button>
        </div>
    );
}

function EventsListSection({ events, hasZip, goAdd, eventsFrom, eventsTo, setEventsFrom, setEventsTo, todayIso, eventTypes, setEventTypes }) {
    const [ctrl, setCtrl] = usePersistedCtrl("eventsList", { sort: "date", dir: "asc", pageSize: 5, page: 0 });
    const [toOpen, setToOpen] = useState(false);
    const fields = hasZip ? [["date", "Date"], ["distance", "Distance"], ["name", "Name"]] : [["date", "Date"], ["name", "Name"]];
    const sorted = sortItems(events || [], ctrl.sort, ctrl.dir);
    const paged = sorted.slice(ctrl.page * ctrl.pageSize, ctrl.page * ctrl.pageSize + ctrl.pageSize);
    const empty = !events || events.length === 0;
    return (
        <div>
            <SectionHeader label="Events" color="#22c55e" icon={Calendar} stickyTop="var(--tsi-list-top, 112px)">
                <div className="flex flex-wrap items-center gap-2 order-3 md:order-2 basis-full md:basis-auto" data-testid="events-list-date-range">
                    <div className="flex items-stretch border border-[#2a2a2a] bg-[#141414]/80">
                        <label htmlFor="events-list-from" className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] px-2.5 flex items-center border-r border-[#2a2a2a] bg-[#0f0f0f] cursor-pointer">From</label>
                        <input id="events-list-from" type="date" data-testid="events-list-from-input" value={eventsFrom} max={eventsTo || undefined}
                            onChange={(e) => setEventsFrom(e.target.value)}
                            className={`h-9 bg-transparent text-sm px-2 focus-visible:outline-none ${eventsFrom && eventsFrom !== todayIso ? "text-[#ff4500] cal-orange" : "text-white cal-gray"}`} />
                        <button type="button" data-testid="events-list-from-reset" title="Reset to today" onClick={() => setEventsFrom(todayIso)}
                            className="px-2 flex items-center text-[#a0a0a0] hover:text-[#ff4500] border-l border-[#2a2a2a]"><Eraser className="h-3.5 w-3.5" /></button>
                    </div>
                    {(toOpen || eventsTo) ? (
                        <div className="flex items-stretch border border-[#2a2a2a] bg-[#141414]/80">
                            <label htmlFor="events-list-to" className="text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] px-2.5 flex items-center border-r border-[#2a2a2a] bg-[#0f0f0f] cursor-pointer">To</label>
                            <input id="events-list-to" type="date" data-testid="events-list-to-input" value={eventsTo} min={eventsFrom || undefined} autoFocus={toOpen && !eventsTo}
                                onChange={(e) => setEventsTo(e.target.value)}
                                className={`h-9 bg-transparent text-sm px-2 focus-visible:outline-none ${eventsTo ? "text-[#ff4500] cal-orange" : "text-white cal-gray"}`} />
                            <button type="button" data-testid="events-list-to-reset" title="Clear — show all upcoming" onClick={() => { setEventsTo(""); setToOpen(false); }}
                                className="px-2 flex items-center text-[#a0a0a0] hover:text-[#ff4500] border-l border-[#2a2a2a]"><Eraser className="h-3.5 w-3.5" /></button>
                        </div>
                    ) : (
                        <button type="button" data-testid="events-list-to-open" title="Set an end date" onClick={() => setToOpen(true)}
                            className="h-9 inline-flex items-center gap-1.5 border border-[#2a2a2a] bg-[#141414]/80 px-2.5 text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0] hover:text-[#ff4500] hover:border-[#3a3a3a]">
                            To <Calendar className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                {!empty && <ListControls testid="events-list-controls" ctrl={ctrl} setCtrl={setCtrl} fields={fields} leading={<EventTypeDropdown selected={eventTypes} onChange={setEventTypes} />} className="flex flex-wrap items-center gap-2 ml-auto order-2 md:order-3" />}
            </SectionHeader>
            {empty ? (
                <div className="border border-[#2a2a2a] py-8 text-center" data-testid="events-list-empty">
                    <div className="text-sm text-[#a0a0a0]">No events match. <button type="button" onClick={goAdd} className="text-[#22c55e] hover:underline uppercase tracking-wider text-xs">Add an event</button></div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 gap-3" data-testid="events-list-grid">
                        {paged.map((e) => {
                            const evPast = (e.end_date || e.event_date || "") < todayLocalISO();
                            const canc = !!e.cancelled_at;
                            return (
                            <RowLink key={e.id} to={`/events/${e.detail_event_id || e.id}`} testid={`events-list-card-${e.id}`} color="#22c55e">
                                <ThumbCol src={e.photo} fallback={(e.name || "?")[0]} color={canc ? "#ef4444" : "#22c55e"} distance={e.distance_miles} bg="black" fit="contain" />
                                <div className="min-w-0 flex-1">
                                    <div className="min-w-0">
                                        <span className={`font-display text-xl tracking-wide break-words ${canc ? "text-red-500" : "group-hover:text-[#22c55e]"}`}>{e.name}</span>
                                        {canc && <span className="text-red-500 font-sans text-xs normal-case tracking-normal ml-2" data-testid={`events-list-cancelled-label-${e.id}`}>(Cancelled?)</span>}
                                    </div>
                                    <div className="text-xs"><span className={(canc || evPast) ? "text-red-500" : "text-[#a0a0a0]"}>{formatDateRange(e.event_date, e.end_date)}{e.start_time ? ` · ${formatTimeRange(e.start_time, e.end_time)}${tzAbbrev(e.timezone, e.event_date) ? " " + tzAbbrev(e.timezone, e.event_date) : ""}` : ""}</span></div>
                        {(eventAudienceParts(e).length > 0 || e.city || e.state) && <div className="text-xs text-[#a0a0a0] truncate"><EventAudience e={e} />{eventAudienceParts(e).length > 0 && (e.city || e.state) ? " · " : null}{[e.city, e.state].filter(Boolean).join(", ")}</div>}
                        {e.review_count > 0 && <StarRating rating={e.avg_rating} count={e.review_count} />}
                                    {canc && <CancelledMeta e={e} />}
                                </div>
                            </RowLink>
                            );
                        })}
                    </div>
                    <Pager testid="events-list-pager" ctrl={ctrl} setCtrl={setCtrl} total={events.length} />
                </>
            )}
        </div>
    );
}

function Thumb({ src, fallback, color, bg, fit = "cover" }) {
    const [failed, setFailed] = useState(false);
    const bgClass = bg === "black" ? "bg-black" : bg === "clear" ? "bg-transparent" : "bg-white";
    const fitClass = fit === "contain" ? "object-contain" : "object-cover";
    if (src && !failed) return <img src={src} alt="" onError={() => setFailed(true)} className={`h-14 w-14 ${fitClass} border border-[#2a2a2a] shrink-0 ${bgClass}`} />;
    return <span className="h-14 w-14 shrink-0 flex items-center justify-center border border-[#2a2a2a] font-display text-lg" style={{ color }}>{fallback}</span>;
}

// Thumbnail with the distance (rounded to whole miles) tucked directly beneath it.
// Distance stays blank until a valid search ZIP produces a distance_miles value.
// `outOfRange` (players only): false = in range (green), true = out of range (red).
function ThumbCol({ src, fallback, color, distance, outOfRange, bg, fit }) {
    const distColor = outOfRange === true ? "#ef4444" : outOfRange === false ? "#22c55e" : "#cfcfcf";
    return (
        <div className="flex flex-col items-center shrink-0 gap-1">
            <Thumb src={src} fallback={fallback} color={color} bg={bg} fit={fit} />
            {distance != null && (
                <span className="text-[11px] leading-none tabular-nums font-semibold" style={{ color: distColor }} data-testid="row-distance">{Math.round(distance)} mi</span>
            )}
        </div>
    );
}

function RowLink({ to, testid, color, children, onClick }) {
    return (
        <Link to={to} data-testid={testid} onClick={onClick} className="group flex items-center gap-4 border border-[#2a2a2a] bg-[#141414] p-3 transition-colors" style={{ borderLeft: `3px solid ${color}` }}>
            {children}
        </Link>
    );
}

// ---- Search dropdown: all matching listings (events soonest-first, then teams by
// rating, then trainers by rating), rendered with the exact same cards as the list. ----
function SearchDropdown({ search, sport, eventsFrom, nearZip, myLoc, onClose, onViewMap, onViewList }) {
    const panelRef = useRef(null);
    const [maxH, setMaxH] = useState(420);
    const [data, setData] = useState({ teams: [], trainers: [], events: [], loading: true });
    const seq = useRef(0);

    // Dedicated GLOBAL search fetch — only the typed term + sport (ignores distance/state/
    // age/grade/gender/circuit filters). Events exclude the past via from_date=eventsFrom
    // (which defaults to today; set a past From date to include past events). This is far
    // faster than reusing the main page's heavily-filtered fetch and never shows stale data.
    // Location (precise coords preferred, else a valid 5-digit ZIP) is passed through so the
    // result cards can show a distance just like the main list/detail pages.
    const geoLat = myLoc?.lat;
    const geoLng = myLoc?.lng;
    const validZip = /^\d{5}$/.test((nearZip || "").trim()) ? nearZip.trim() : "";
    useEffect(() => {
        const q = (search || "").trim();
        if (!q) return undefined;
        const geo = {};
        if (validZip) geo.near_zip = validZip;
        if (geoLat != null && geoLng != null) { geo.near_lat = geoLat; geo.near_lng = geoLng; }
        const myId = ++seq.current;
        const fresh = () => seq.current === myId;
        setData((d) => ({ ...d, loading: true }));
        const t = setTimeout(() => {
            const teamsP = api.get("/teams", { params: { search: q, sport, ...geo } }).then((r) => r.data).catch(() => []);
            const trList = api.get("/trainer-listings", { params: { search: q, sport, ...geo } }).then((r) => r.data).catch(() => []);
            const eventsP = api.get("/events", { params: { search: q, sport, when: "upcoming", from_date: eventsFrom || undefined, ...geo } }).then((r) => r.data).catch(() => []);
            Promise.all([teamsP, trList, eventsP]).then(([teams, listings, events]) => {
                if (!fresh()) return;
                const ql = q.toLowerCase();
                const tagged = (listings || []).map((l) => ({ ...l, _listing: true }))
                    .filter((l) => `${l.name || ""} ${l.city || ""} ${l.state || ""}`.toLowerCase().includes(ql));
                setData({ teams: groupIntoPrograms(teams || []), trainers: tagged, events: events || [], loading: false });
            });
        }, 200);
        return () => clearTimeout(t);
    }, [search, sport, eventsFrom, validZip, geoLat, geoLng]);

    useEffect(() => {
        const recompute = () => {
            if (!panelRef.current) return;
            const rect = panelRef.current.getBoundingClientRect();
            setMaxH(Math.max(220, window.innerHeight - rect.top - 16));
        };
        recompute();
        window.addEventListener("resize", recompute);
        window.addEventListener("scroll", recompute, true);
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target) && !e.target.closest("form")) onClose();
        };
        const t = setTimeout(() => document.addEventListener("mousedown", onDown), 0);
        return () => {
            clearTimeout(t);
            window.removeEventListener("resize", recompute);
            window.removeEventListener("scroll", recompute, true);
            window.removeEventListener("keydown", onKey);
            document.removeEventListener("mousedown", onDown);
        };
    }, [onClose]);

    const rating = (x) => x.avg_rating ?? x.review_rating ?? 0;
    const dateKey = (e) => e.event_date || e.end_date || "9999-12-31";
    const sortedEvents = [...data.events].sort((a, b) => (dateKey(a) < dateKey(b) ? -1 : dateKey(a) > dateKey(b) ? 1 : 0));
    const sortedTeams = [...data.teams].sort((a, b) => rating(b) - rating(a));
    const sortedTrainers = [...data.trainers].sort((a, b) => rating(b) - rating(a));
    const total = sortedEvents.length + sortedTeams.length + sortedTrainers.length;

    return (
        <div
            ref={panelRef}
            data-testid="search-overlay"
            className="absolute left-0 right-0 top-full -mt-px z-[900] border border-t-0 border-[#2a2a2a] bg-[#141414] shadow-[0_24px_48px_rgba(0,0,0,0.7)] flex flex-col"
            style={{ maxHeight: maxH }}
        >
            <div className="flex-1 overflow-y-auto min-h-0" data-testid="search-overlay-results">
                <div className="p-2 space-y-2">
                    {data.loading ? (
                        <div className="py-16 flex items-center justify-center gap-2 text-sm text-[#a0a0a0]" data-testid="search-overlay-loading">
                            <Loader2 className="h-4 w-4 animate-spin" /> Searching…
                        </div>
                    ) : total === 0 ? (
                        <div className="py-16 text-center text-sm text-[#a0a0a0]" data-testid="search-overlay-empty">No matches.</div>
                    ) : (
                        <>
                            {sortedEvents.map((e) => <EventCard key={`e-${e.id}`} e={e} testid={`search-row-event-${e.id}`} onNav={onClose} />)}
                            {sortedTeams.map((p) => <TeamCard key={`t-${p.key}`} p={p} testid={`search-row-team-${p.key}`} onNav={onClose} />)}
                            {sortedTrainers.map((tr) => <TrainerCard key={`r-${tr._listing ? "l" : ""}${tr.id}`} tr={tr} testid={`search-row-trainer-${tr.id}`} onNav={onClose} />)}
                        </>
                    )}
                </div>
            </div>
            <div className="shrink-0 border-t border-[#2a2a2a] bg-[#0c0c0c] grid grid-cols-2 gap-2 p-2">
                <button type="button" data-testid="search-overlay-view-map" onClick={onViewMap} className="h-11 inline-flex items-center justify-center gap-2 bg-[#ff4500] text-white text-xs uppercase tracking-[0.16em] font-semibold hover:bg-[#ff5722] transition-colors">
                    View on Map{total ? ` · ${total}` : ""}
                </button>
                <button type="button" data-testid="search-overlay-view-list" onClick={onViewList} className="h-11 inline-flex items-center justify-center gap-2 border border-[#2a2a2a] bg-[#1a1a1a] text-white text-xs uppercase tracking-[0.16em] font-semibold hover:border-[#ff4500] transition-colors">
                    View as List
                </button>
            </div>
        </div>
    );
}

